import { formatHandicap, memberInitials } from '../../group_leader/helpers/player'
import { sharedT } from '../../group_leader/helpers/i18n'
import { to12hr } from '../../group_leader/helpers/time'
import { competitionNeedsTeeSheet } from './games'
import { playsWomensTee } from './players'

// From the kit, re-exported so the step's components take every time helper from one module.
export { to12hr }

// Pure logic behind the Pairings step: time conversion, the slot model, the drag ghost.

export const GROUP_SIZES = [ '2', '3', '4', '5', '6' ]

export const hasTeamGame = (specs) => (specs || [])
  .some((spec) => competitionNeedsTeeSheet(spec?.node_selections?.competition?.name))

// set_tee_times_with_interval's own cap; above it a run is written row by row.
export const MAX_RUN_INTERVAL_MINUTES = 60

// Empty groups restored on leaving walk-on: what a fresh Pairings step starts with.
export const WALKON_RESTORE_GROUPS = 2

// A walk-on's hole type ('1' all 18, '2' front 9, '3' back 9) sets the start hole and the virtual tee.
export const walkonStartingHole = (holeType) => (String(holeType) === '3' ? 10 : 1)

// A nine-hole tee has no front/back to pick between; the endpoint wants 'Nine Holes' for it.
export const NINE_HOLES = 'nine'

// The monolith stores '1'/'2'/'3' only, so this wizard's own NINE_HOLES is saved as the whole course.
export const walkonHoleTypeForSave = (holeType) => (
  String(holeType) === NINE_HOLES ? '1' : String(holeType)
)

// Which holes a tee plays; a nine-hole twin covers whichever half of hole_data is filled in.
export const holeTypeFromTee = (tee) => {
  if (!tee) { return null }
  if (!tee.parent_id) { return tee.nine_hole_course ? NINE_HOLES : '1' }
  const par = tee.hole_data?.par || []
  return par.slice(9).some((value) => value !== null && value !== undefined) ? '3' : '2'
}

const HOLES_TYPES = { 2: 'Front 9', 3: 'Back 9', [NINE_HOLES]: 'Nine Holes' }
export const holesTypeFor = (holeType) => HOLES_TYPES[holeType] || 'All 18'

// The Course & Tee step's names for each hole type, which Review reads back.
export const HOLES_PLAYED_KEYS = {
  1: 'walkon.holes_18', 2: 'walkon.holes_front9', 3: 'walkon.holes_back9', [NINE_HOLES]: 'walkon.holes_nine',
}

// The start hole as a number, mapping the legacy 'hole1'/'hole10'/'split' selector values.
export const startHoleNumber = (value) => {
  if (value === 'hole10') { return 10 }
  if (value === 'hole1' || value === 'split') { return 1 }
  const hole = Number(value)
  return Number.isInteger(hole) && hole > 0 ? hole : 1
}

// Holes on the event's course from its own labels, 18 until they arrive.
export const courseHoleCount = (course) => {
  const labels = course?.attributes?.hole_labels
  if (Array.isArray(labels) && labels.length > 0) { return labels.length }
  return 18
}

// Shotgun direction read off the sheet (nothing stores it): second row a hole above or below; null if unclear.
export const shotgunReversed = (holes, holeCount) => {
  const [ first, second ] = (holes || []).filter((hole) => Number.isInteger(hole) && hole > 0)
  if (!first || !second || holeCount < 2) { return null }
  const wrap = (hole) => ((hole - 1 + holeCount) % holeCount) + 1
  if (second === wrap(first + 1)) { return false }
  if (second === wrap(first - 1)) { return true }
  return null
}

// Free holes in the order this shotgun would fill them, continuing from its last row (1/18/17 gives 16).
export const freeHolesInOrder = (placedHoles, holeCount) => {
  const holes = (placedHoles || []).map(Number)
    .filter((hole) => Number.isInteger(hole) && hole > 0 && hole <= holeCount)
  const taken = new Set(holes)
  const all = Array.from({ length: holeCount }, (_, index) => index + 1)
  const reversed = shotgunReversed(holes, holeCount)
  const last = holes[holes.length - 1]
  if (reversed === null || !last) { return all.filter((hole) => !taken.has(hole)) }

  const step = reversed ? -1 : 1
  return all.map((_, index) => (((last - 1 + step * (index + 1)) % holeCount) + holeCount) % holeCount + 1)
    .filter((hole) => !taken.has(hole))
}

// Hour and minute from "8:15" or the shorthands people type ("8", "815", "0815"); null if it does not parse.
const splitHourMinute = (text) => {
  const withColon = text.match(/^(\d{1,2}):(\d{2})$/)
  if (withColon) { return [ Number(withColon[1]), Number(withColon[2]) ] }
  if (!/^\d{1,4}$/.test(text)) { return null }

  // One or two digits is an hour on its own; longer, the last two are always the minutes.
  return text.length <= 2
    ? [ Number(text), 0 ]
    : [ Number(text.slice(0, -2)), Number(text.slice(-2)) ]
}

export const to24hr = (time12) => {
  if (!time12) { return null }
  const match = time12.trim().match(/^(.+?)\s*(AM|PM)$/i)
  if (!match) { return null }

  const parts = splitHourMinute(match[1].trim())
  if (!parts) { return null }

  const [ hour, minute ] = parts
  // Bounds here, not in the pattern, as "875" is easy to type; hour 0 round-trips through to12hr.
  if (hour > 12 || minute > 59) { return null }

  let h = hour
  const period = match[2].toUpperCase()
  if (period === 'PM' && h !== 12) { h += 12 }
  if (period === 'AM' && h === 12) { h = 0 }
  return `${String(h).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

// Steps a 24-hour time by minutes; null rather than crossing midnight, as a tee sheet has no second day.
export const shiftTime24 = (time24, minutes) => {
  if (!time24) { return null }
  const [ h, m ] = time24.split(':').map(Number)
  if (Number.isNaN(h) || Number.isNaN(m)) { return null }
  const total = h * 60 + m + minutes
  if (total < 0 || total >= 1440) { return null }
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

// Minutes from one time to the next; a zero or negative gap means the rows are not a run.
export const minutesBetween = (fromTime24, toTime24) => {
  if (!fromTime24 || !toTime24) { return null }
  const [ fh, fm ] = fromTime24.split(':').map(Number)
  const [ th, tm ] = toTime24.split(':').map(Number)
  if ([ fh, fm, th, tm ].some(Number.isNaN)) { return null }
  return (th * 60 + tm) - (fh * 60 + fm)
}

// The sheet's interval when every gap agrees; three rows at least, as one gap cannot disagree.
export const runInterval = (times24) => {
  const gaps = (times24 || []).slice(1).map((time, index) => minutesBetween(times24[index], time))
  if (gaps.length < 2 || gaps[0] <= 0) { return null }
  return gaps.every((gap) => gap === gaps[0]) ? gaps[0] : null
}

// Frozen, so an unhandled poll outcome cannot fall through to the success path.
export const REORGANIZE_OUTCOME = Object.freeze({
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
})

export const REORGANIZE_POLL_INTERVAL_MS = 2000
export const REORGANIZE_POLL_MAX_ATTEMPTS = 150

// A small drag card instead of the whole row; it must be in the document when setDragImage runs.
export const buildDragGhost = (name, handicapIndex, initials) => {
  const el = document.createElement('div')
  Object.assign(el.style, {
    position: 'fixed', top: '-1000px', left: '-1000px',
    background: '#fff', border: '2px solid #E8623A', borderRadius: '10px',
    padding: '8px 12px', boxShadow: '0 6px 20px rgba(0,0,0,.22)',
    display: 'flex', alignItems: 'center', gap: '10px',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    pointerEvents: 'none', maxWidth: '210px',
  })
  const av = document.createElement('div')
  Object.assign(av.style, {
    width: '28px', height: '28px', borderRadius: '50%', flexShrink: '0',
    background: '#E8623A', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '10px', fontWeight: '700',
  })
  av.textContent = initials || memberInitials(name)
  const info = document.createElement('div')
  Object.assign(info.style, { minWidth: '0', flex: '1' })
  const nm = document.createElement('div')
  Object.assign(nm.style, {
    fontSize: '12px', fontWeight: '600', color: '#1A1A1A',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '140px',
  })
  nm.textContent = name
  info.appendChild(nm)
  const hi = formatHandicap(handicapIndex)
  if (hi !== null) {
    const hiEl = document.createElement('div')
    Object.assign(hiEl.style, { fontSize: '11px', color: '#767676', fontWeight: '400' })
    hiEl.textContent = sharedT('hi_display', { value: hi })
    info.appendChild(hiEl)
  }
  el.appendChild(av)
  el.appendChild(info)
  return el
}

// A golfer can inherit a tee from another course via an empty group Reorganize stamped, unseen.
const sameCourse = (player, courseId) =>
  !courseId || String(player?.tee?.course_id || '') === String(courseId)

// The wizard's course, else the one most of the field plays -- counted, so one stray cannot decide.
export const fieldCourseId = (players, preferredCourseId = null) => {
  if (preferredCourseId) { return String(preferredCourseId) }

  const counts = {}
  players.forEach((player) => {
    const courseId = player?.tee?.course_id
    if (!courseId) { return }
    counts[String(courseId)] = (counts[String(courseId)] || 0) + 1
  })

  const ranked = Object.entries(counts).sort(([ , a ], [ , b ]) => b - a)
  return ranked.length > 0 ? ranked[0][0] : ''
}

// For a golfer with no tee, or one on another course. From the sheet first: it survives a reload.
export const fieldTeeId = (players, { courseId, configTeeId = null } = {}) => {
  const onCourse = players.find((player) => player?.tee?.id && sameCourse(player, courseId))
  return String(onCourse?.tee?.id || configTeeId || '')
}

// Grouped by tee, one call each; everyone, as re-writing a tee also puts it on the group for a shotgun.
export const teeWritesForField = (players, { courseId, fallbackTeeId, fallbackTees = null } = {}) => {
  const byTee = {}

  players.forEach((player) => {
    const playerRoundId = String(player?.player_round_id || '')
    if (!playerRoundId) { return }

    const ownTeeId = player?.tee?.id ? String(player.tee.id) : ''
    // A tee-less golfer takes their own gender's tee, not the first placed golfer's.
    const fallback = fallbackTees
      ? (playsWomensTee(player) ? fallbackTees.women : fallbackTees.men)
      : fallbackTeeId
    const teeId = (ownTeeId && sameCourse(player, courseId)) ? ownTeeId : String(fallback || '')
    if (!teeId) { return }

    if (!byTee[teeId]) { byTee[teeId] = [] }
    byTee[teeId].push(playerRoundId)
  })

  return Object.entries(byTee)
}
